import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import MediaRelationsService from '../../services/mediarelations.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { Stack, InputLabel, Input, LinearProgress, Box, Modal } from "@mui/material";
import { FluxButton, FluxIcon } from '@meltwater/flux-web-components-react';
import styles from './mediarelations.module.css';
import csvDownload from 'json-to-csv-export';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { useMediaRelationsData } from './mediarelations';
import DataTable from '../../components/DataTable';

const Outreach = ({ load, setActiveTab }) => {
    const [recipientListData, setRecipientListData] = useState(null);
    const [recipientsToRecover, setRecipientsToRecover] = useState(null);
    const [recoverRecipientListName, setRecoverRecipientListName] = useState(null);
    const [mediarelationspermissions, setMediarelationspermissions] = useState([]);
    const [listModalOpen, setListModalOpen] = useState(false);
    const [recoverListModalOpen, setRecoverListModalOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState('info');
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    const { companyId } = useCompanyId();
    const { 
        outreachData, 
        setOutreachData, 
        setLoading,
        apiAttempts,
        markApiAttempt,
        resetApiAttempt 
    } = useMediaRelationsData();
    
    const loadingTimerRef = useRef(null);
    const landingPageNavRef = useRef(false);
    const isRequestInProgressRef = useRef(false);
    const isMountedRef = useRef(true);
    
    // Custom guide steps specific to Outreach
    const outreachGuideSteps = useMemo(() => [
        {
            title: 'Outreach Management',
            text: 'This guide will help you navigate the Outreach features.',
            element: null,
        },
        {
            title: 'Search Outreach',
            text: 'Use the search box to quickly find outreach emails by subject or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export Outreach Stats',
            text: 'You can export all outreach stats or selected outreach stats using these buttons.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'View Lists Button',
            text: 'Click this button to view recipient lists associated with an outreach email.',
            element: 'button.MuiButtonBase-root.FluxButton-root',
            position: { top: 100, left: 500 }
        },
        {
            title: 'Recipient List Actions',
            text: 'When viewing recipient lists, you can export recipients or restore lists as needed.',
            element: null,
        }
    ], []);

    const handleOpenViewOutreachRecipientsModal = useCallback(async (outreachId) => {
        console.log(`Viewing lists for outreach ${outreachId}`);
        setIsFetching(true);
        try {
            const recipientLists = await MediaRelationsService.getOutreachRecipients(companyId, outreachId);
            setRecipientListData(recipientLists.data);
            setListModalOpen(true);
            setFeedbackMsg(`Successfully loaded recipient lists.`);
            setAlertSeverity('success');
        } catch (error) {
            console.error(`Error loading recipient lists: ${error.message}`);
            setFeedbackMsg(`Error loading recipient lists: ${error.message}`);
            setAlertSeverity('error');
            setOpenSnackBar(true);
        }
        setIsFetching(false);
    }, [companyId]);

    // Track initial load to identify direct navigation from landing page
    useEffect(() => {
        if (load && !landingPageNavRef.current) {
            console.log("Outreach tab activated first time from landing page");
            landingPageNavRef.current = true;
        }
    }, [load]);

    // Fetch data function separated for reuse
    const fetchData = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isRequestInProgressRef.current) {
            console.log("Request already in progress, skipping duplicate call");
            return;
        }

        try {
            isRequestInProgressRef.current = true;
            setIsLoading(true);
            setLoading(true);
            setLoadingMessage('Fetching outreach emails...');
            loadingTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
                }
            }, 15000);
            
            console.log("Initiating getOutreachEmails call for company:", companyId);
            const response = await MediaRelationsService.getOutreachEmails(companyId);
            console.log("getOutreachEmails call successful");
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setOutreachData(response.data);
                setFeedbackMsg(`Successfully loaded ${response.data.length} outreach emails.`);
                setAlertSeverity('success');
                setOpenSnackBar(true);
                setHasError(false);
                
                // Mark that we've successfully fetched data
                markApiAttempt('outreach');
            }
            
            return response.data;
        } catch (error) {
            console.error("Error in getOutreachEmails API call:", error);
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setOpenSnackBar(true);
                const errorMsg = error.message || 'Unknown error';
                setFeedbackMsg(`Error loading data: ${errorMsg}, Please refresh or contact support.`);
                setErrorMessage(errorMsg);
                setAlertSeverity('error');
                setHasError(true);
            }
            throw error;
        } finally {
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            if (isMountedRef.current) {
                setLoadingMessage('');
                setIsLoading(false);
                setLoading(false);
            }
        }
    }, [companyId, setLoading, setOutreachData, markApiAttempt]);

    // Effect for initial setup
    useEffect(() => {
        const jwtToken = getJwtToken();
        setIsFetching(false);
        
        if (jwtToken) {
            const { mediarelationspermissions } = jwt_decode(jwtToken);
            setMediarelationspermissions(mediarelationspermissions);
        }

        // Track component mount status
        isMountedRef.current = true;
        
        return () => {
            isMountedRef.current = false;
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, []);

    // Separate effect for data loading
    useEffect(() => {
        if (!load || !companyId) {
            return;
        }

        async function loadData() {
            // Check if we have cached data
            if (outreachData) {
                console.log("Using cached outreach data");
                setIsLoading(false);
                return;
            }
            
            // Check if this component has already attempted to load data and had an error
            if (hasError && apiAttempts.outreach) {
                console.log("Skipping API call due to previous error");
                return;
            }
            
            // Prevent multiple requests
            if (isRequestInProgressRef.current) {
                console.log("Request already in progress, skipping");
                return;
            }
            
            // Fetch new data if needed
            try {
                console.log("No cached data found, fetching fresh data");
                await fetchData();
            } catch (error) {
                console.error("Error loading outreach data:", error);
            }
        }
        
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, companyId, outreachData, hasError, apiAttempts.outreach]);

    const resetErrorState = () => {
        console.log("Resetting outreach error state to allow retry");
        setHasError(false);
        resetApiAttempt('outreach'); // Reset the API attempt tracking
    };

    const refreshData = async () => {
        // Prevent multiple refresh calls
        if (isRequestInProgressRef.current) {
            console.log("Refresh already in progress, skipping");
            return;
        }

        try {
            resetApiAttempt('outreach'); // Reset API attempt for refresh
            setHasError(false); // Clear any previous errors
            setLoadingMessage('Refreshing outreach data...');
            await fetchData();
            if (isMountedRef.current) {
                setFeedbackMsg('Data refreshed successfully');
                setAlertSeverity('success');
                setOpenSnackBar(true);
            }
        } catch (error) {
            console.error("Error refreshing outreach data:", error);
            if (isMountedRef.current) {
                const errorMsg = error.message || 'Unknown error';
                setErrorMessage(errorMsg);
                setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
                setAlertSeverity('error');
                setOpenSnackBar(true);
            }
        }
    };

    const exportOutreachEmails = async (rows) => {
        setIsFetching(true);
        // Map both IDs and subjects from the row data
        let outreachData = rows.map(row => ({
            id: row.original._id,
            subject: row.original.subject
        }));
        let outreachIds = outreachData.map(item => item.id);
        let payload = { outreachIds: outreachIds };
        let fileName = "outreach-stats.csv";
        try {
            let response = await MediaRelationsService.exportOutreachStats(payload, companyId);
            // Pass the outreach data with subjects to the CSV function
            MediaRelationsService.saveOutreachCsv(response.data, fileName, outreachData);
            setFeedbackMsg(`Successfully exported Outreach stats.`);
            setAlertSeverity("success");
            setOpenSnackBar(true);
        }
        catch (error) {
            console.error(`Error exporting Outreach stats: ${error.message}`);
            setFeedbackMsg(`Error exporting Outreach stats: ${error.message}`);
            setAlertSeverity("error");
            setOpenSnackBar(true);
        }
        setIsFetching(false);
    };

    const handleExportRecipientListData = (row) => {
        const exportdata = [];
        row.recipients.forEach((recipient) => {
            exportdata.push({ listId: row.id, listName: row.name, ...recipient });
        });
        const data = {
            data: exportdata,
            delimiter: ',',
            filename: `recipients-${row.id}`,
        };
        csvDownload(data);
    };

    const handleCloseViewOutreachRecipientsModal = () => {
        setRecipientListData(null);
        setListModalOpen(false);
    };

    const handleCloseSetRecoverListModal = () => {
        setRecoverListModalOpen(false);
        setRecipientsToRecover([]);
    };

    const handleRecoverRecipientList = async () => {
        handleCloseSnackBar();
        setIsFetching(true);
        let payload = { listName: recoverRecipientListName, contactIds: recipientsToRecover.map(item => item.id) };
        try {
            let response = await MediaRelationsService.createMediaList(companyId, payload);
            setFeedbackMsg(`Created list "${response.data.newListName}"`);
            setAlertSeverity("success");
            setOpenSnackBar(true);
        }
        catch (error) {
            console.error(`Error creating list ${payload.listName}. ${error.message}`);
            setFeedbackMsg(`Error creating list "${recoverRecipientListName}": ${error.message}`);
            setAlertSeverity("error");
            setOpenSnackBar(true);
        }
        handleCloseSetRecoverListModal();
        setIsFetching(false);
    };

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
        setFeedbackMsg('');
        setAlertSeverity("info");
    };

    const openRecoverListModal = (recipients) => {
        setRecoverListModalOpen(true);
        setRecipientsToRecover(recipients);
    };

    const modalStyle = {
        backgroundColor: '#ffffff',
        padding: '20px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "80%",
        boxShadow: 24,
        p: 4,
        height: "80%",
    };

    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    tint="primary"
                    type="filled"
                    onClick={() => exportOutreachEmails(table.getPrePaginationRowModel().rows)}
                >
                    Export All Outreach Stats
                </FluxButton>
                <FluxButton
                    tint="accent"
                    type="filled"
                    onClick={() => exportOutreachEmails(table.getSelectedRowModel().rows)}
                    disabled={
                        !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                    }
                >
                    Export Selected Outreach Stats
                </FluxButton>
                <FluxButton
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
            </Stack>
        );
    };

    // Main outreach table columns
    const columns = useMemo(
        () => [
            {
                header: 'Subject',
                accessorKey: 'subject',
                enableGlobalFilter: true
            },
            {
                header: 'id',
                accessorKey: '_id',
                enableGlobalFilter: true
            },
            {
                header: 'From Address',
                accessorKey: 'fromAddress',
                enableGlobalFilter: true
            },
            {
                header: 'Sent Date',
                accessorKey: 'scheduledDate',
            },
            {
                header: 'Recipient Lists',
                accessorFn: (row) => `${row._id}`,
                Cell: ({ row }) => (
                    <FluxButton
                        tint="primary"
                        type="filled"
                        onClick={() => { handleOpenViewOutreachRecipientsModal(row.original._id); }}
                    >
                        View Lists
                    </FluxButton>
                )
            }
        ],
        [handleOpenViewOutreachRecipientsModal]
    );

    // Recipient list modal columns
    const recipientListColumns = useMemo(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
            },
            {
                header: 'Name',
                accessorKey: 'name',
            },
            {
                header: 'Number of Recipients',
                accessorKey: 'count',
            },
            {
                header: 'Export List',
                accessorFn: (row) => row.recipients,
                Cell: ({ row }) => (
                    <FluxButton
                        tint="primary"
                        type="filled"
                        onClick={() => handleExportRecipientListData(row.original)}
                    >
                        Export Recipients
                    </FluxButton>
                )
            },
            {
                header: 'Recover List',
                accessorFn: (row) => row.recipients,
                Cell: ({ cell }) => (
                    <FluxButton
                        tint="accent"
                        type="filled"
                        onClick={() => openRecoverListModal(cell.getValue())}
                    >
                        Restore List
                    </FluxButton>
                )
            }
        ],
        [],
    );

    if (!mediarelationspermissions || !mediarelationspermissions.includes('read')) {
        return <div>You do not have permission to access this page.</div>;
    }

    return (
        <div className={styles.Search}>
                {isFetching && (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress color="secondary" />
                    </Box>
                )}

                <DataTable 
                    columns={columns}
                    data={outreachData || []}
                    isLoading={isLoading && !hasError}
                    loadingMessage={loadingMessage || 'Loading outreach data...'}
                    hasError={hasError}
                    errorMessage={errorMessage}
                    errorTitle="Error Loading Outreach Data"
                    onBackClick={() => setActiveTab(null)}
                    onTryAgain={() => {
                        resetErrorState();
                        refreshData();
                    }}
                    renderToolbarActions={renderToolbarActions}
                    backTooltip="Return to Dashboard"
                    showGuide={false}
                    guideSteps={outreachGuideSteps}
                    snackbarProps={{
                        open: openSnackBar,
                        onClose: handleCloseSnackBar,
                        message: feedbackMsg,
                        severity: alertSeverity,
                        autoHideDuration: 10000
                    }}
                    tableProps={{
                        muiSearchTextFieldProps: {
                            placeholder: 'Search outreach...',
                            variant: 'standard',
                            sx: { width: '300px' }
                        },
                        initialState: { 
                            density: 'compact',
                            columnVisibility: { "_id": false },
                            pagination: { pageSize: 50 }
                        }
                    }}
                />
            {/* Recipient lists modal */}
            {listModalOpen && recipientListData && (
                <div className={styles.dataDivContainer}>
                    <Stack direction='row' spacing={4} justifyContent='flex-end' alignContent={"right"}>
                        <FluxButton onClick={handleCloseViewOutreachRecipientsModal} type='text'>
                            <FluxIcon name='actions:close'></FluxIcon>
                        </FluxButton>
                    </Stack>
                    <DataTable
                        columns={recipientListColumns}
                        data={recipientListData}
                        tableProps={{
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            enableColumnActions: false,
                            initialState: { 
                                density: 'compact', 
                                columnVisibility: { 
                                    recoverLists: mediarelationspermissions.includes('write') 
                                }, 
                                pagination: { pageSize: 100 } 
                            }
                        }}
                    />
                </div>
            )}

            {/* Recover list modal */}
            {recoverListModalOpen && (
                <Modal
                    open={recoverListModalOpen}
                    onClose={handleCloseSetRecoverListModal}
                    aria-labelledby='modal-modal-title'
                    aria-describedby='modal-modal-description'
                >
                    <Box style={{ ...modalStyle, width: "30%", height: "auto" }}>
                        <Stack direction='row' spacing={4} justifyContent='flex-end' alignContent={"right"}>
                            <FluxButton onClick={handleCloseSetRecoverListModal} type='text'>
                                Cancel
                            </FluxButton>
                        </Stack>
                        <Stack direction='column' spacing={1} justifyContent='flex-start' alignItems='flex-start'>
                            <InputLabel>New Media List Name</InputLabel>
                            <Input fullWidth type="text" onChange={(evt) => { setRecoverRecipientListName(evt.target.value) }}></Input>
                        </Stack>
                        <Stack style={{}} direction='row' paddingTop={2} spacing={4} justifyContent='flex-end' alignItems='flex-end'>
                            <FluxButton tint="primary" type="filled" onClick={handleRecoverRecipientList}>
                                Submit
                            </FluxButton>
                        </Stack>
                    </Box>
                </Modal>
            )}
        </div>
    );
};

export default Outreach;